/**
 * @file Listens and handles events that create new posts on dropping
 * of some object onto a component.
 */
import { trackEvent } from 'bits/analytics'
import { $ } from 'bits/jquery'
import { isUrl } from 'bits/url'
import { mapActions, mapGetters } from 'vuex'

export default {
  data() {
    return {
      dropToPostSubscribed: false,
      $_dropToPostMixin_callback: null,
      // We started seeing users selecting text from posts AND UI elements on the padlet
      // and dropping them onto the same padlet. These dragged texts would then become a post, leading to a lot
      // of confusion because they would contain weird text like "Avatar of X".
      //
      // By detecting and toggling this flag to true, we can block creation of posts
      // from text that has been dragged from on the padlet itself.
      // Dragging text from off the padlet onto the padlet will still work.
      //
      // Detection happens on the `dragstart` event when dropToPost is enabled.
      hasStartedDraggingUIOnThisPage: false,
    }
  },
  computed: {
    ...mapGetters(['isUsingNewComposerModal']),
    ...mapGetters('map', ['isDraggingLocationPickerPin']),
  },
  methods: {
    ...mapActions('post', ['createPost']),
    $_dropToPostMixin_processDrop(e) {
      e.preventDefault()
      const wishListElement = document.getElementById('wish-list')
      let left = 0,
        top = 0
      if (wishListElement) {
        left = Math.max(e.originalEvent.pageX - wishListElement.getBoundingClientRect().left, 0)
        top = Math.max(e.originalEvent.pageY - wishListElement.getBoundingClientRect().top, 0)
      } else {
        left = Math.max(e.originalEvent.pageX, 0)
        top = Math.max(e.originalEvent.pageY, 0)
      }

      let offset = 0

      if (e.originalEvent.dataTransfer) {
        // try files
        let files = e.originalEvent.dataTransfer.files
        if (files && files.length > 0) {
          for (var i = 0; i < files.length; i++) {
            let file = files[i]
            this.$_dropToPostMixin_processFileDrop(file, top + offset, left + offset)
            offset += 8
          }
        } else {
          // a non-file has been likely dragged
          // try link
          let link = e.originalEvent.dataTransfer.getData('text/uri-list')
          if (link) {
            this.$_dropToPostMixin_processLinkDrop(link, top, left)
          } else {
            let text = e.originalEvent.dataTransfer.getData('text/plain')
            if (text) {
              if (isUrl(text)) {
                this.$_dropToPostMixin_processLinkDrop(text, top, left)
              } else {
                // don't post if the text is actually dragged from on this page.
                if (this.hasStartedDraggingUIOnThisPage) return
                this.$_dropToPostMixin_processTextDrop(text, top, left)
              }
            }
          }
        }
      }

      return false
    },

    $_dropToPostMixin_processFileDrop(file, top, left) {
      this.$_dropToPostMixin_track('File')
      const newPost = {
        attributes: {
          file: file,
          top: top,
          left: left,
        },
      }
      this.$_dropToPostMixin_callback ? this.$_dropToPostMixin_callback(newPost) : this.createPost(newPost)
    },

    $_dropToPostMixin_processLinkDrop(link, top, left) {
      this.$_dropToPostMixin_track('Link')
      const newPost = {
        attributes: {
          attachment: link,
          top: top,
          left: left,
        },
      }
      this.$_dropToPostMixin_callback ? this.$_dropToPostMixin_callback(newPost) : this.createPost(newPost)
    },

    $_dropToPostMixin_processTextDrop(text, top, left) {
      this.$_dropToPostMixin_track('Text')
      const newPost = {
        attributes: {
          body: text,
          top: top,
          left: left,
        },
      }
      this.$_dropToPostMixin_callback ? this.$_dropToPostMixin_callback(newPost) : this.createPost(newPost)
    },

    $_dropToPostMixin_track(type) {
      trackEvent('Posts', 'add', `${type} drop`)
    },

    enableDropToPost(wallContainerEl, callback) {
      const DRAGOVER_CLASS = 'is-dragover'
      const $element = $(wallContainerEl || '#wall-container')
      if (this.dropToPostSubscribed) return
      this.$_dropToPostMixin_callback = callback
      $element.on('dragover.droptopost', (e) => {
        if (this.isUsingNewComposerModal && !this.isDraggingLocationPickerPin) {
          // let the drag event go through and handled by the new drag and drop overlay + vuexstore
          // in case a location picker pin (in map format) is dragged, still handle it using this mixin for now
          return true
        }
        $element.addClass(DRAGOVER_CLASS)
        return false
      })
      $element.on('dragleave.droptopost dragend.droptopost', (e) => {
        $element.removeClass(DRAGOVER_CLASS)
        return false
      })
      $element.on('drop.droptopost', (e) => {
        $element.removeClass(DRAGOVER_CLASS)
        this.$_dropToPostMixin_processDrop(e)
        // unset flag that indicates that a user has started
        // dragging on this page.
        this.hasStartedDraggingUIOnThisPage = false
      })
      // dragstart is not triggered when the user is dragging items
      // from outside the page, e.g. another app, or another browser.
      $element.on('dragstart.droptopost', (e) => {
        // Set flag that indicates a user has started dragging some kind of UI element
        // on the page.
        //
        // We detect text (wish title/body), textarea (comment body), and img (avatars).
        // Note: that the reordering of posts also trigger a dragstart event but because
        // we've namespaced the event and its handler, it doesn't affect ordering of
        // posts.
        const nodeType = e.originalEvent.target.nodeName.toLowerCase()
        if (nodeType === '#text' || nodeType === 'textarea' || nodeType === 'img') {
          this.hasStartedDraggingUIOnThisPage = true
        }
      })
      this.dropToPostSubscribed = true
    },

    disableDropToPost(wallContainerEl) {
      const $element = $(wallContainerEl || '#wall-container')
      this.$_dropToPostMixin_callback = null
      if (!this.dropToPostSubscribed) return
      $element.off('.droptopost')
      this.dropToPostSubscribed = false
    },
  },
}
